import { useEffect, useRef, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Radio, MapPin, Car, Flag, Smartphone } from "lucide-react";
import { simhubAPI } from "@/lib/api";

const POLL_MS = 500;
// Snapshot is considered "stale" (user paused the mobile app, left the
// screen, or SimHub stopped sending) once the mobile has been silent longer
// than this (ms). We still render the last known values dimmed with a
// yellow "PAUSED" pill. Anything older than DEAD_AFTER_MS is treated as
// disconnected.
const STALE_AFTER_MS = 3000;
const DEAD_AFTER_MS = 15000;

const formatLap = (ms) => {
  if (!ms || ms <= 0) return "--:--.---";
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const millis = Math.floor(ms % 1000);
  return `${minutes}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`;
};

const formatGap = (ms) => {
  if (!ms || ms === 0) return "—";
  const sign = ms > 0 ? "+" : "-";
  const abs = Math.abs(ms);
  const sec = Math.floor(abs / 1000);
  const millis = Math.floor(abs % 1000);
  return `${sign}${sec}.${String(millis).padStart(3, "0")}`;
};

function Tile({ label, children, big }) {
  return (
    <Card className="border-border bg-card p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className={`font-display font-bold leading-none text-foreground ${big ? "mt-1 text-5xl" : "mt-1 text-2xl"}`}>
        {children}
      </div>
    </Card>
  );
}

export default function SimHubRelayDashboard() {
  const [snapshot, setSnapshot] = useState(null);
  const [status, setStatus] = useState("disconnected"); // live | paused | disconnected
  const timerRef = useRef(null);

  const pollOnce = useCallback(async () => {
    try {
      const res = await simhubAPI.getLatestTelemetry();
      const data = res.data || {};
      if (!data.snapshot) {
        setStatus("disconnected");
        setSnapshot(null);
        return;
      }
      setSnapshot(data.snapshot);
      const age = data.age_ms || 0;
      if (age <= STALE_AFTER_MS) setStatus("live");
      else if (age <= DEAD_AFTER_MS) setStatus("paused");
      else setStatus("disconnected");
    } catch {
      setStatus("disconnected");
    }
  }, []);

  useEffect(() => {
    pollOnce();
    timerRef.current = window.setInterval(pollOnce, POLL_MS);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [pollOnce]);

  const dash = "--";
  const t = snapshot || {};
  const rpm = Math.round(t.rpm || 0);
  const maxRpm = Math.round(t.maxRpm || 8000);
  const rpmPct = snapshot ? Math.min(100, Math.max(0, (rpm / maxRpm) * 100)) : 0;

  let pillLabel = "DISCONNECTED";
  let pillDot = "bg-red-500";
  if (status === "live") { pillLabel = "LIVE"; pillDot = "bg-emerald-500"; }
  else if (status === "paused") { pillLabel = "PAUSED"; pillDot = "bg-amber-500"; }

  const noMobile = !snapshot;

  return (
    <Card className="overflow-hidden border-border bg-card" data-testid="simhub-relay-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-secondary/30 px-4 py-3">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-primary" />
          <p className="font-display text-sm font-bold uppercase tracking-wide">Live Dashboard</p>
          <Badge className="gap-1 border-0 bg-background/40 text-[10px] font-bold uppercase" data-testid="simhub-relay-status">
            <span className={`h-1.5 w-1.5 rounded-full ${pillDot}`} /> {pillLabel}
          </Badge>
        </div>
      </div>

      {noMobile ? (
        <div className="flex flex-col items-center gap-2 p-8 text-center">
          <Smartphone className="h-8 w-8 text-muted-foreground" />
          <p className="font-semibold text-foreground">Waiting for your mobile app</p>
          <p className="max-w-md text-xs text-muted-foreground">
            Open the Sim Racing Connected app on your phone and launch the SimHub tab. Live telemetry will appear here within a second.
          </p>
        </div>
      ) : (
        <div className="space-y-3 p-4">
          {/* Session banner */}
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3 text-primary" />
              <span className="font-semibold text-foreground">{t.trackDisplayName || t.trackName || dash}</span>
              {t.trackConfig && <span className="opacity-70"> · {t.trackConfig}</span>}
            </span>
            <span className="inline-flex items-center gap-1">
              <Car className="h-3 w-3 text-primary" />
              <span className="font-semibold text-foreground">{t.carModel || dash}</span>
              {t.carClass && <span className="opacity-70"> · {t.carClass}</span>}
            </span>
            <span className="inline-flex items-center gap-1">
              <Flag className="h-3 w-3 text-primary" /> {t.sessionType || dash}
            </span>
          </div>

          {/* Top row: Speed · Gear · RPM */}
          <div className="grid grid-cols-3 gap-3">
            <Tile label="Speed" big>
              {snapshot ? Math.round(t.speed || 0) : dash}
              <span className="ml-1 text-xs font-semibold text-muted-foreground">km/h</span>
            </Tile>
            <Card className="flex flex-col items-center justify-center border-border bg-card p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Gear</p>
              <p className="font-display text-6xl font-black leading-none text-primary">{t.gear || dash}</p>
            </Card>
            <Tile label="RPM" big>{snapshot ? rpm.toLocaleString() : dash}</Tile>
          </div>

          {/* RPM bar */}
          <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full bg-gradient-to-r from-primary via-primary to-destructive transition-all duration-100"
              style={{ width: `${rpmPct}%` }}
            />
          </div>

          {/* Position / Lap / Fuel / Assists */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Card className="border-border bg-card p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Position</p>
              <p className="mt-1 font-display text-2xl font-bold">
                {snapshot ? `P${t.position || "-"}` : dash}
                {t.totalCars ? <span className="ml-1 text-xs text-muted-foreground">/ {t.totalCars}</span> : null}
              </p>
              <div className="mt-1 flex gap-3 text-[10px] text-muted-foreground">
                <span>▲ {snapshot ? formatGap(t.gapAhead) : dash}</span>
                <span>▼ {snapshot ? formatGap(t.gapBehind) : dash}</span>
              </div>
            </Card>
            <Tile label="Lap">{snapshot ? `${t.currentLap || 0}${t.totalLaps ? ` / ${t.totalLaps}` : ""}` : dash}</Tile>
            <Tile label="Fuel">
              {snapshot && t.fuelLevel ? Number(t.fuelLevel).toFixed(1) : dash}
              <span className="ml-1 text-xs font-semibold text-muted-foreground">%</span>
            </Tile>
            <Card className="border-border bg-card p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Assists</p>
              <div className="mt-1 flex items-center gap-2 text-xs font-semibold">
                <span>ABS: <span className="text-foreground">{t.absLevel ?? dash}</span></span>
                <span>TC: <span className="text-foreground">{t.tcLevel ?? dash}</span></span>
              </div>
            </Card>
          </div>

          {/* Lap times */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="border-border bg-card p-3" data-testid="simhub-relay-current-lap">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Current</p>
              <p className="mt-1 font-mono text-xl font-bold tabular-nums text-primary">{snapshot ? formatLap(t.currentLapTime) : dash}</p>
            </Card>
            <Card className="border-border bg-card p-3" data-testid="simhub-relay-last-lap">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Last</p>
              <p className="mt-1 font-mono text-xl font-bold tabular-nums">{snapshot ? formatLap(t.lastLapTime) : dash}</p>
            </Card>
            <Card className="border-border bg-card p-3" data-testid="simhub-relay-best-lap">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Best</p>
              <p className="mt-1 font-mono text-xl font-bold tabular-nums text-emerald-400">{snapshot ? formatLap(t.bestLapTime) : dash}</p>
            </Card>
          </div>
        </div>
      )}
    </Card>
  );
}
